import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { reportsApi } from '../lib/api'
import type { ReportRequest, ReportResult } from '../types/reporting'
import type { JobEnqueuedDto } from '../types/job'

export function useReportDefinitions() {
  return useQuery({
    queryKey: ['reports', 'definitions'],
    queryFn: () => reportsApi.getDefinitions().then((r) => r.data),
    // The catalogue is static code data on the server; no need to refetch often.
    staleTime: 60 * 60 * 1000,
  })
}

// Preview is a synchronous read (no job, no cache to invalidate) — it just renders whatever the
// server hands back, so a plain mutation is the right shape rather than a query.
export function useReportPreview() {
  return useMutation<ReportResult, unknown, ReportRequest>({
    mutationFn: (request) => reportsApi.preview(request).then((r) => r.data),
  })
}

// Generating a snapshot enqueues a background job (202 + jobId) rather than returning a result —
// the caller polls useJobPolling(jobId) and invalidates/navigates once the job reaches Succeeded.
export function useCreateReportRun() {
  return useMutation<JobEnqueuedDto, unknown, ReportRequest>({
    mutationFn: (request) => reportsApi.createRun(request).then((r) => r.data),
  })
}

export function useReportRun(id: string | undefined) {
  return useQuery({
    queryKey: ['reports', 'runs', id],
    queryFn: () => reportsApi.getRun(id as string).then((r) => r.data),
    enabled: !!id,
    // A frozen run never changes — no reason to refetch it.
    staleTime: Infinity,
  })
}

// The index of saved snapshots. Key shares the ['reports', 'runs', ...] prefix with
// useReportRun above — React Query's invalidateQueries match is prefix-based (exact: false by
// default), so ReportRunnerPage's `invalidateQueries({ queryKey: ['reports', 'runs'] })` on a
// successful generation invalidates this list too, not just the single-run cache.
export function useReportRuns(take = 50) {
  return useQuery({
    queryKey: ['reports', 'runs', { take }],
    queryFn: () => reportsApi.listRuns(take).then((r) => r.data),
  })
}

export function useDeleteReportRun() {
  const queryClient = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: (id) => reportsApi.deleteRun(id).then(() => undefined),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reports', 'runs'] }),
  })
}
